import { ReactNode } from "react"

export type StateOption = {
  code: string
  label: string
}

function BlueNotice({
  children,
  className = ""
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 ${className}`}
    >
      {children}
    </div>
  )
}

function Checkbox({
  checked,
  disabled,
  onChange
}: {
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="mt-0.5 h-5 w-5 shrink-0 rounded-sm border-gray-300 text-dantzBlue focus:ring-dantzBlue disabled:cursor-not-allowed disabled:opacity-50"
    />
  )
}

function Radio({
  checked,
  disabled,
  name,
  onChange
}: {
  checked: boolean
  disabled: boolean
  name: string
  onChange: () => void
}) {
  return (
    <input
      type="radio"
      name={name}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="h-4 w-4 shrink-0 border-gray-300 text-dantzBlue focus:ring-dantzBlue disabled:cursor-not-allowed disabled:opacity-50"
    />
  )
}

// Shared "all states / selected states" panel used by both the EP1001
// designation-states form (DesignationStatesSection) and the EP1200
// entry-into-European-phase form (PatentEpPhaseFormPage) for extension and
// validation states. The two forms show different legal notice copy, so
// that's passed in rather than hardcoded here.
export default function StateSelectionPanel({
  groupName,
  allLabel,
  selectedLabel = "Select designated states:",
  options,
  selectedStates,
  selectionMode,
  disabled = false,
  notice,
  footnote,
  onSelectionModeChange,
  onStateToggle
}: {
  groupName: string
  allLabel: string
  selectedLabel?: string
  options: StateOption[]
  selectedStates: string[]
  selectionMode: "all" | "selected"
  disabled?: boolean
  notice: ReactNode
  footnote?: ReactNode
  onSelectionModeChange: (mode: "all" | "selected") => void
  onStateToggle: (code: string, checked: boolean) => void
}) {
  const allSelected = selectionMode === "all"

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-5">
      <BlueNotice className="mb-5 leading-relaxed">{notice}</BlueNotice>

      <div className="mb-5 flex flex-wrap gap-x-8 gap-y-3 text-sm text-gray-700">
        <label className="inline-flex cursor-pointer items-center gap-2">
          <Radio
            name={groupName}
            checked={allSelected}
            disabled={disabled}
            onChange={() => !disabled && onSelectionModeChange("all")}
          />
          {allLabel}
        </label>

        <label className="inline-flex cursor-pointer items-center gap-2">
          <Radio
            name={groupName}
            checked={selectionMode === "selected"}
            disabled={disabled}
            onChange={() => !disabled && onSelectionModeChange("selected")}
          />
          {selectedLabel}
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {options.map((option) => (
          <label
            key={option.code}
            className={`inline-flex items-center gap-2 text-sm text-gray-700 ${
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
          >
            <Checkbox
              checked={allSelected || selectedStates.includes(option.code)}
              disabled={disabled || allSelected}
              onChange={(checked) => onStateToggle(option.code, checked)}
            />
            <span>
              {option.code} - {option.label}
            </span>
          </label>
        ))}
      </div>

      {footnote && <BlueNotice className="mt-5">{footnote}</BlueNotice>}
    </div>
  )
}
